import { set } from "lodash";
import { queryClient } from "src/lib/react-query";
import {
  type SegmentsOrderBy,
  useAudienceFilterOptionsQuery,
  useAudienceLabelsQuery,
  useAudienceParentQuery,
  useAudienceQuery,
  useAudiencesQuery,
  useListFoldersQuery,
  useViewsQuery,
} from "src/graphql";
import {
  type SortDirection,
  DEFAULT_SORT_DIRECTION,
  DEFAULT_TABLE_LIMIT,
} from "src/ui/table/use-table-config";
import { AUDIENCE_SORT_OPTIONS, DEFAULT_AUDIENCE_SORT_KEY } from "./constants";
import {
  buildAudienceHasuraFilters,
  parseAudienceFilterParams,
} from "./filters";

/**
 * Fire-and-forget loader for /audiences/:id.
 * Starts prefetching audience data while the lazy chunk downloads,
 * so queries are already in the React Query cache when the component mounts.
 */
export const audienceDetailLoader = async ({
  params,
}: {
  params: { id?: string };
}) => {
  const id = params.id;
  if (!id) return null;

  void Promise.all([
    queryClient.fetchQuery({
      queryKey: useAudienceQuery.getKey({ id }),
      queryFn: useAudienceQuery.fetcher({ id }),
    }),
    queryClient.fetchQuery({
      queryKey: useAudienceParentQuery.getKey({ audience_id: id }),
      queryFn: useAudienceParentQuery.fetcher({ audience_id: id }),
      meta: { isFastEndpoint: true },
    }),
  ]);

  return null;
};

/**
 * Build the `useAudiencesQuery` variables the component will fire on mount
 * from URL search params, so the prefetched payload matches the component's
 * cache key.
 *
 * The builder defaults to the flag-off case (`enableRealtimeAudiences:
 * false`), which adds the `is_realtime_audience: { _eq: false }` predicate.
 * Users with the flag on will cache-miss this one query; the other four
 * prefetches still hit.
 */
export const buildAudiencesListQueryVariables = (
  searchParams: URLSearchParams,
) => {
  const sortKeyParam = searchParams.get("sortKey");
  const sortKey =
    sortKeyParam && (AUDIENCE_SORT_OPTIONS as string[]).includes(sortKeyParam)
      ? sortKeyParam
      : DEFAULT_AUDIENCE_SORT_KEY;
  const sortDirParam = searchParams.get("sortDirection");
  const sortDirection: SortDirection =
    sortDirParam === "asc" || sortDirParam === "desc"
      ? sortDirParam
      : DEFAULT_SORT_DIRECTION;

  const filterParams = parseAudienceFilterParams(searchParams);

  return {
    filters: buildAudienceHasuraFilters(filterParams),
    offset: 0,
    limit: DEFAULT_TABLE_LIMIT,
    orderBy: set<SegmentsOrderBy>({}, sortKey, sortDirection),
  };
};

const FOLDERS_VARIABLES = {
  resourceType: "models" as const,
  folderType: "audiences" as const,
};

const VIEWS_VARIABLES = { resourceType: "audiences" };

/**
 * Fire-and-forget loader for /audiences.
 * Prefetches the audiences table query (with URL filters applied), filter
 * options, labels, folders, and saved views while the audiences list chunk
 * downloads.
 */
export const audiencesListLoader = async ({
  request,
}: {
  request: Request;
}) => {
  const { searchParams } = new URL(request.url);
  const variables = buildAudiencesListQueryVariables(searchParams);

  void Promise.all([
    queryClient.fetchQuery({
      queryKey: useAudiencesQuery.getKey(variables),
      queryFn: useAudiencesQuery.fetcher(variables),
      meta: { isFastEndpoint: true },
    }),
    queryClient.fetchQuery({
      queryKey: useAudienceFilterOptionsQuery.getKey(),
      queryFn: useAudienceFilterOptionsQuery.fetcher(),
    }),
    queryClient.fetchQuery({
      queryKey: useAudienceLabelsQuery.getKey(),
      queryFn: useAudienceLabelsQuery.fetcher(),
    }),
    queryClient.fetchQuery({
      queryKey: useListFoldersQuery.getKey(FOLDERS_VARIABLES),
      queryFn: useListFoldersQuery.fetcher(FOLDERS_VARIABLES),
      meta: { isFastEndpoint: true },
    }),
    queryClient.fetchQuery({
      queryKey: useViewsQuery.getKey(VIEWS_VARIABLES),
      queryFn: useViewsQuery.fetcher(VIEWS_VARIABLES),
    }),
  ]);

  return null;
};
